<template>
  <DSSearchDialog
    :open="isOpen"
    :query="searchStore.string"
    :filters
    :active-filter-key="searchStore.currentTag"
    :sections
    :scope-label="scopeLabel"
    @update:open="value => !value && handleClose()"
    @update:query="value => (searchStore.string = value)"
    @update:active-filter-key="value => (searchStore.currentTag = value)"
    @select="handleSelect"
  />
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { computed, onMounted, onWatcherCleanup, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import { ModalClass, SearchSummary } from '@types'

import { ROUTE_NAME } from '@/const'
import { DEBOUNCE_DELAY, SEARCH_TAG_ALL, SEARCH_TAG_TRANSACTIONS } from '@/const/common'

import { handleCatchedError } from '@/helpers/common'

import { useSearchStore } from '@/store/search'
import { prepareTransactionsSearchList } from '@/store/search/utils/helpers'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import { useGlobalSearch } from '@/hooks/globalSearch'

import { DSSearchDialog } from '@ds'
import type { DSSearchDialogFilter, DSSearchDialogSection } from '@ds'
import { ArrowLeftRightIcon, ChartColumnIcon, BanknoteArrowDownIcon, BanknoteArrowUpIcon, FolderIcon, TagsIcon } from '@lucide/vue'

type Props = {
  modal: ModalClass<any>
}

const props = defineProps<Props>()

const searchStore = useSearchStore()
const assetsBunchStore = useAssetsBunchStore()
const transactionsStore = useTransactionsStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const { handleSearchResult } = useGlobalSearch()

const isOpen = computed(() => props.modal.isOpened)

const foundTransactions = ref<SearchSummary[]>([])

const CATEGORY_ICON: Record<string, Component> = {
  Assets: ChartColumnIcon,
  Tags: TagsIcon,
  Documents: FolderIcon,
  'Income accounts': BanknoteArrowUpIcon,
  'Expense accounts': BanknoteArrowDownIcon,
  [SEARCH_TAG_TRANSACTIONS]: ArrowLeftRightIcon,
}

const CATEGORY_ORDER = ['Assets', 'Tags', 'Documents', 'Expense accounts', 'Income accounts', SEARCH_TAG_TRANSACTIONS]

const filters = computed<DSSearchDialogFilter[]>(() => {
  const hasQuery = searchStore.string.trim() !== ''
  const tags = [...searchStore.tags]
  if (foundTransactions.value.length) tags.push(SEARCH_TAG_TRANSACTIONS)
  if (tags.length > 1 && !tags.includes(SEARCH_TAG_ALL)) tags.unshift(SEARCH_TAG_ALL)
  return tags.map(key => ({
    key,
    label: key,
    count: hasQuery ? filterCount(key) : undefined,
  }))
})

const filterCount = (key: string) => {
  if (key === SEARCH_TAG_ALL) {
    return Object.values(searchStore.getCounts).reduce((sum, count) => sum + count, 0) + foundTransactions.value.length
  }
  if (key === SEARCH_TAG_TRANSACTIONS) return foundTransactions.value.length
  return searchStore.getCounts[key as keyof typeof searchStore.getCounts] ?? 0
}

const scopeLabel = computed(() =>
  searchStore.currentTag === SEARCH_TAG_ALL ? 'assets, tags, documents and transactions' : searchStore.currentTag,
)

/**
 * Items are filtered by `currentTag` in the store; `foundTransactions` is merged client-side.
 * Since `filterFunction` returns all items on empty query, handle that case specially to return an empty list.
 */
const combinedItems = computed<SearchSummary[]>(() => {
  if (searchStore.string.trim() === '') return []
  const items = [...searchStore.getSearchSummary]
  if ([SEARCH_TAG_ALL, SEARCH_TAG_TRANSACTIONS].includes(searchStore.currentTag)) {
    items.push(...foundTransactions.value)
  }
  return items
})

const itemsByKey = computed(() => {
  const map = new Map<string, SearchSummary>()
  combinedItems.value.forEach((item, index) => {
    map.set(rowKey(item, index), item)
  })
  return map
})

const rowKey = (item: SearchSummary, index: number) =>
  item.id ? `${item.category}-${item.id}` : `${item.category}-${index}-${item.result}`

/** The ticker/type/currency-ish remainder of `result` once its own title is stripped out, canonical-style " · " joined. */
const buildSubtitle = (item: SearchSummary) => {
  const rest = item.result
    .split(', ')
    .filter(part => part && part !== item.title)
    .join(' · ')
  if (item.additional) return rest ? `${rest} · ${item.additional}` : item.additional
  return rest || item.category || ''
}

const sections = computed<DSSearchDialogSection[]>(() =>
  CATEGORY_ORDER.map(category => {
    const items = combinedItems.value
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.category === category)
    return {
      key: category,
      label: category,
      items: items.map(({ item, index }) => ({
        key: rowKey(item, index),
        icon: CATEGORY_ICON[category] ?? ChartColumnIcon,
        title: item.title || item.result,
        subtitle: buildSubtitle(item),
      })),
    }
  }).filter(section => section.items.length),
)

const handleClose = () => {
  props.modal.close()
}

const handleSelect = (key: string) => {
  const item = itemsByKey.value.get(key)
  if (!item) return
  handleClose()
  handleSearchResult(item)
}

const fetchTransactions = useDebounceFn(async (signal: AbortSignal) => {
  const value = searchStore.string.trim()
  if (!value) {
    foundTransactions.value = []
    return
  }
  try {
    const transactions = await transactionsStore.fetch({ search: value }, false, signal, false)
    foundTransactions.value = transactions.data.map(
      prepareTransactionsSearchList(
        SEARCH_TAG_TRANSACTIONS,
        ROUTE_NAME.TRANSACTIONS_ITEM,
        transactionsSettingsStore.getList,
        assetsBunchStore.getNameById,
      ),
    )
  } catch (e) {
    if (signal.aborted) return
    handleCatchedError(e as string, searchStore.string)
    foundTransactions.value = []
  }
}, DEBOUNCE_DELAY)

watch(
  () => searchStore.string,
  () => {
    const controller = new AbortController()
    fetchTransactions(controller.signal)
    onWatcherCleanup(() => controller.abort())
  },
  { immediate: true },
)

onMounted(() => {
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch()
  }
})
</script>
